import clsx from 'clsx';
import { forwardRef } from 'react';

// Lib
import { SIZES, SIZE_25, SIZE_33, SIZE_50, SIZE_75, SIZE_FULL } from '@/lib/gridElements/size';

// Types
import type { ComponentPropsWithRef, ComponentPropsWithoutRef } from 'react';

export const GRID_STYLES = {
    DEFAULT: 'default',
    MEDIA_VIEWER: 'media-viewer'
} as const;

export type GridStyleType = (typeof GRID_STYLES)[keyof typeof GRID_STYLES];

export type GridPropsType = ComponentPropsWithoutRef<'main'> & {
    gridStyle?: GridStyleType;
};

const Grid = ({ gridStyle = GRID_STYLES.DEFAULT, className, ...restProps }: GridPropsType) => (
    <main
        className={clsx(
            'col-span-full grid w-full grid-cols-12',
            {
                'gap-5': gridStyle === GRID_STYLES.DEFAULT,
                'gap-x-3 gap-y-2.5 md:gap-x-5 md:gap-y-4 lg:gap-y-5':
                    gridStyle === GRID_STYLES.MEDIA_VIEWER
            },
            className
        )}
        {...restProps}
    />
);

const itemSize = {
    [GRID_STYLES.DEFAULT]: {
        [SIZE_25]: 'col-span-full sm:col-span-6 lg:col-span-3',
        [SIZE_33]: 'col-span-full sm:col-span-6 lg:col-span-4',
        [SIZE_50]: 'col-span-full xl:col-span-6',
        [SIZE_75]: 'col-span-full lg:col-span-9',
        [SIZE_FULL]: 'col-span-full'
    },
    [GRID_STYLES.MEDIA_VIEWER]: {
        [SIZE_25]: 'col-span-3',
        [SIZE_33]: 'col-span-4',
        [SIZE_50]: 'col-span-6',
        [SIZE_FULL]: 'col-span-full'
    }
} as const;

type GridItemProps = ComponentPropsWithRef<'div'> & {
    size?: (typeof SIZES)[number];
    gridStyle?: GridStyleType;
};

const Item = forwardRef<HTMLDivElement, GridItemProps>(
    ({ size = SIZE_25, gridStyle = GRID_STYLES.DEFAULT, className, ...restProps }, ref) => {
        return (
            <div
                className={clsx(
                    'h-full',
                    itemSize[gridStyle][size],
                    {
                        'min-h-[220px]': gridStyle === GRID_STYLES.DEFAULT,
                        'min-h-[160px]': gridStyle === GRID_STYLES.MEDIA_VIEWER
                    },
                    className
                )}
                ref={ref}
                {...restProps}
            />
        );
    }
);

Item.displayName = 'GridItem';

Grid.Item = Item;

export default Grid;
